import React, { forwardRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import Markdown from 'markdown-to-jsx';

import VisuallyHidden from '@atlaskit/visually-hidden';
import { Box, Stack, xcss } from '@atlaskit/primitives';

import { AITelepointerWrapper } from '@atlassian/ai-components';
import { type FollowUpObject } from '@atlassian/ai-agent-streaming';

import { SummaryPopupScrollableBody } from '../shared-components/SummaryPopupScrollableBody';
import { QuickSummaryFollowUpContainer } from '../QuickSummaryFollowUpContainer';
import { AISummaryFooter } from '../shared-components/AISummaryFooter';
import type { SummaryAnalyticsPropertiesType } from '../shared-components/summarySharedTypes';

const styles = xcss({ height: '100%' });

const FooterObjectSidebarStyles = xcss({
	// Object sidebar padding is 16px, this ensures that the footer isn't cutoff by parent padding
	width: 'calc(100% + 32px)',
	marginLeft: 'space.negative.200',
});

const i18n = defineMessages({
	quickSummaryAudioHeader: {
		id: 'quick-summary.content.audio-header',
		defaultMessage: 'Summary',
		description: 'Visually hidden header to notify audio users of the intent of the summary popup',
	},
});

type QuickSummaryComponentProps = {
	analyticsProperties: SummaryAnalyticsPropertiesType;
	content: string;
	contentId: string;
	contentPath: string;
	contentType: string;
	followUpContent?: FollowUpObject[];
	hideFooter: boolean;
	hideTelepointer: boolean;
	isRovoEnabled: boolean;
	setIsOpen: (open: boolean) => void;
	spaceKey?: string;
	isInObjectSidebar?: boolean;
};

export const QuickSummaryComponent = forwardRef<HTMLDivElement, QuickSummaryComponentProps>(
	(
		{
			analyticsProperties,
			content,
			contentId,
			contentPath,
			contentType,
			followUpContent,
			hideFooter,
			hideTelepointer,
			isRovoEnabled,
			setIsOpen,
			spaceKey,
			isInObjectSidebar,
		},
		summaryRef,
	) => {
		const { formatMessage } = useIntl();

		return (
			<Stack spread="space-between" xcss={styles}>
				<VisuallyHidden role="heading">
					{formatMessage(i18n.quickSummaryAudioHeader)}
				</VisuallyHidden>
				<Box padding="space.250">
					<Stack space="space.250" spread="space-between">
						<SummaryPopupScrollableBody
							useMaxHeight={!isInObjectSidebar}
							ref={summaryRef}
							data-testid="quick-summary-body-content"
						>
							<AITelepointerWrapper hideTelepointer={hideTelepointer}>
								<Markdown>{content}</Markdown>
							</AITelepointerWrapper>
						</SummaryPopupScrollableBody>
						<QuickSummaryFollowUpContainer
							content={content}
							contentPath={contentPath}
							setIsOpen={setIsOpen}
							isPageSummaryFollowUpEnabled={isRovoEnabled}
							followUpContent={followUpContent}
						/>
					</Stack>
				</Box>
				<Box xcss={isInObjectSidebar && FooterObjectSidebarStyles}>
					<AISummaryFooter
						analyticsProperties={analyticsProperties}
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						summaryText={content}
						isHidden={hideFooter}
					/>
				</Box>
			</Stack>
		);
	},
);
